import clsx from 'clsx';
import {
  formatTonnageBtu,
  tonnageTileClassName
} from 'components/common/recommendation-tiles/tonnage-section-helpers';
import {
  CalcTonnage,
  EffectiveTonnage,
  EquipmentTonnage,
  equipmentVersusCalc,
  isUnsureTonnage,
  sizingPathAfterSizeTile,
  toggleTonnageTile,
  toggleUnsureTonnageRecord,
  tonnageRangeLabel,
  TonnageRecord,
  TONNAGE_TILE_TONS,
  tonnageSpanContains,
  tonnageSpansOverlap,
  visibleTonnageSelection
} from 'utils/tonnage-logic';

type TonnageSectionProps = Readonly<{
  effective: EffectiveTonnage | null;
  equipment: EquipmentTonnage | null;
  calc: CalcTonnage | null;
  record: TonnageRecord | null;
  onRecordChange: (record: TonnageRecord) => void;
  showSizingPath: boolean;
}>;

export default function TonnageSection({
  effective,
  equipment,
  calc,
  record,
  onRecordChange,
  showSizingPath
}: TonnageSectionProps) {
  const unsure = isUnsureTonnage(record);
  const selected = visibleTonnageSelection(effective, record);

  const handleTileClick = (value: number) => {
    const tons = toggleTonnageTile(selected, value);
    onRecordChange({
      tons,
      source: 'manual',
      ...sizingPathAfterSizeTile(tons, record, showSizingPath)
    });
  };

  const handleUnsureClick = () => {
    onRecordChange(toggleUnsureTonnageRecord(record));
  };

  let hint: React.ReactNode;
  if (unsure) {
    hint = (
      <span className='text-gray-600'>
        We&apos;ll figure out your system size on the next page.
      </span>
    );
  } else if (!effective) {
    hint = (
      <span className='text-gray-500'>
        {showSizingPath
          ? 'Select a size, or pick Unsure to get one from Layout & Sizing.'
          : 'Select a size, or get one from Layout & Sizing.'}
      </span>
    );
  } else if (effective.source === 'equipment' && equipment) {
    hint = (
      <span className='text-gray-600'>
        <span className='font-semibold'>From your equipment:</span> your{' '}
        {equipment.unitLabel}
        {equipment.modelNumber ? ` (${equipment.modelNumber})` : ''} is a{' '}
        {equipment.raw}-ton unit. Adjust freely — this is a starting point, not
        a lock.
      </span>
    );
  } else if (effective.source === 'calc' && calc) {
    // The load is the size story; when the old unit sits outside the sizes
    // the load takes, say so — that is why the tiles don't show its size.
    const oldUnit = equipmentVersusCalc(equipment, calc);
    hint = (
      <span className='text-gray-600'>
        <span className='font-semibold'>Calculated:</span>{' '}
        {formatTonnageBtu(calc.btu)} BTU → {tonnageRangeLabel(calc.tons)} — from
        your Layout &amp; Sizing calculation.
        {oldUnit && equipment && (
          <span className='text-gray-500'>
            {' '}
            Your existing {equipment.raw}-ton {equipment.unitLabel} is {oldUnit}{' '}
            than your load needs.
          </span>
        )}
      </span>
    );
  } else {
    hint = (
      <span className='text-gray-600'>
        <span className='font-semibold'>
          {effective.source === 'union'
            ? 'Your pick + calculation'
            : 'Your pick'}
        </span>
        <span className='text-gray-500'> — adjust or clear anytime.</span>
      </span>
    );
  }
  const matchesCalc =
    !unsure &&
    !!effective &&
    !!calc &&
    effective.source !== 'calc' &&
    tonnageSpansOverlap(effective.tons, calc.tons);

  return (
    <div className='mt-3' data-testid='tonnage-section'>
      <div className='mb-1 flex items-baseline gap-2'>
        <span className='text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
          System Size (Tons)
        </span>
        {!unsure && effective && (
          <span
            data-testid='tonnage-readout'
            className='text-xs font-semibold text-blue-700'
          >
            {tonnageRangeLabel(effective.tons)}
          </span>
        )}
      </div>
      <div className='flex flex-wrap items-center gap-1.5'>
        {TONNAGE_TILE_TONS.map((value) => {
          const isSelected = tonnageSpanContains(selected, value);
          return (
            <button
              key={value}
              type='button'
              aria-pressed={isSelected}
              onClick={() => handleTileClick(value)}
              className={clsx(tonnageTileClassName(isSelected), 'w-12 px-0')}
            >
              {value}
            </button>
          );
        })}
        {showSizingPath && (
          <button
            type='button'
            data-testid='tonnage-unsure'
            aria-pressed={unsure}
            onClick={handleUnsureClick}
            className={clsx(tonnageTileClassName(unsure), 'px-3')}
          >
            Unsure
          </button>
        )}
      </div>
      <div
        data-testid='tonnage-hint'
        className='mt-2 border-l-2 border-blue-300 bg-gray-50 px-2 py-1.5 text-xs'
      >
        {hint}
        {matchesCalc && (
          <span className='text-gray-500'> · matches your calculation</span>
        )}
      </div>
    </div>
  );
}
